import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Zap, Gift, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const PromoSignup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    promo_code: searchParams.get('code') || ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [codeValidated, setCodeValidated] = useState(false);
  const [codeInfo, setCodeInfo] = useState(null);
  const [error, setError] = useState('');

  const validateCode = async () => {
    if (!formData.promo_code) return;
    
    try {
      const response = await axios.get(`${API}/admin/promo-codes/validate/${formData.promo_code}?plan=pro`);
      if (response.data.valid && response.data.discount_type === 'no_cb') {
        setCodeValidated(true);
        setCodeInfo(response.data);
        setError('');
      } else if (response.data.valid && response.data.discount_type !== 'no_cb') {
        setError('Ce code requiert une carte bancaire. Utilisez la page de tarifs standard.');
        setCodeValidated(false);
      } else {
        setError(response.data.message || 'Code invalide');
        setCodeValidated(false);
      }
    } catch (err) {
      setError('Code promo invalide ou expiré');
      setCodeValidated(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!codeValidated) {
      toast.error('Veuillez d\'abord valider votre code promo');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/promo-code-signup`, formData);
      
      // Store token and user
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Compte créé avec succès !');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur lors de la création du compte';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Zap className="w-8 h-8 text-primary" />
            <span className="text-2xl font-heading font-bold">Boost-StrategIA</span>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-2xl font-heading font-bold mb-2">
              Activer mon accès offert
            </h1>
            <p className="text-muted text-sm">
              Entrez votre code promo pour bénéficier de votre accès gratuit
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Promo Code */}
            <div>
              <label className="text-sm text-muted block mb-2">Code promo</label>
              <div className="flex gap-2">
                <Input
                  value={formData.promo_code}
                  onChange={(e) => {
                    setFormData({...formData, promo_code: e.target.value.toUpperCase()});
                    setCodeValidated(false);
                    setCodeInfo(null);
                  }}
                  placeholder="MONCODE"
                  className="font-mono flex-1"
                  disabled={codeValidated}
                />
                <Button 
                  type="button" 
                  variant={codeValidated ? 'default' : 'outline'}
                  onClick={validateCode}
                  disabled={!formData.promo_code || codeValidated}
                >
                  {codeValidated ? <Check className="w-4 h-4" /> : 'Valider'}
                </Button>
              </div>
              {codeValidated && codeInfo && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg"
                >
                  <p className="text-sm text-blue-400 font-medium">
                    ✨ {codeInfo.discount_text}
                  </p>
                </motion.div>
              )}
            </div>

            {/* Only show rest of form if code is validated */}
            {codeValidated && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4"
              >
                {/* Name */}
                <div>
                  <label className="text-sm text-muted block mb-2">Nom complet</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="text-sm text-muted block mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="jean@example.com"
                    required
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="text-sm text-muted block mb-2">Mot de passe</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Submit */}
                <Button 
                  type="submit" 
                  className="w-full shadow-glow-md"
                  disabled={loading}
                >
                  {loading ? 'Création du compte...' : 'Activer mon accès gratuit'}
                </Button>
              </motion.div>
            )}

            {/* Error for code validation */}
            {error && !codeValidated && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-muted">
            Déjà un compte ?{' '}
            <Link to="/auth" className="text-primary hover:underline">
              Se connecter
            </Link>
          </div>
        </div>

        {/* Info */}
        <p className="text-center text-xs text-muted mt-6">
          En créant un compte, vous acceptez nos{' '}
          <Link to="/terms" className="text-primary hover:underline">CGV</Link>
          {' '}et notre{' '}
          <Link to="/privacy" className="text-primary hover:underline">politique de confidentialité</Link>.
        </p>
      </motion.div>
    </div>
  );
};

export default PromoSignup;
